import { FC, Fragment, createPortal } from "preact/compat";
import { Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import { TrackGroupTile } from "~/core";
import { me, useApp } from "~/core/store";
import { IconButton, TimeDistance } from "~/components";
import { useDialogUrlState, usePortalTarget } from "~/hooks";
import ChevronRightIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import PauseIcon from "@material-design-icons/svg/outlined/pause.svg";
import PlayIcon from "@material-design-icons/svg/outlined/play_arrow.svg";
import ScheduleIcon from "@material-design-icons/svg/outlined/schedule.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const EACH_SECOND = 1000;

const Popup: FC = () => {
    const {
        mobile: { isTrackerOpen, toggleTracker, toggleTrackerPage },
        tracker: {
            loading,
            activeTask,
            activeTile,
            activeExternal,
            tiles,
            stop,
            startTile,
        },
    } = useApp();
    const visibleTiles = tiles.value.filter((tile) => !tile.inactive);
    const target = usePortalTarget();

    useDialogUrlState(isTrackerOpen);

    if (!target.value) {
        return null;
    }

    const active = activeTask.value ?? activeTile.value ?? activeExternal.value;

    const handleStop = () => {
        if (active?._track) {
            stop(active._track);
        }
    };

    const getHandleTileClick = (tile: TrackGroupTile) => () => {
        handleStop();

        if (active?.id !== tile.id) {
            startTile(tile._trackGroup);
        }
    };

    return createPortal(
        <Transition
            as={Fragment}
            show={isTrackerOpen.value}
            enter="transform transition duration-100 ease-in"
            enterFrom="scale-105 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transform transition duration-100 ease-out"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-105 opacity-0"
        >
            <div className="fixed left-[calc(50px+0.5rem)] top-[calc(var(--safe-top)+46px)] z-[70] max-h-[80vh] w-[calc(100vw-74px)] overflow-y-auto rounded-3xl bg-zinc-600/60 border-2 border-zinc-600/20 fill-current text-white shadow-lg">
                <div className="sticky top-0 flex items-center border-b-2 border-zinc-600/20 p-3">
                    <IconButton onClick={handleStop} flat className="p-2">
                        {loading.value ? (
                            <Loader width="18" height="18" />
                        ) : active ? (
                            <PauseIcon width="18" height="18" />
                        ) : (
                            <ScheduleIcon width="18" height="18" />
                        )}
                    </IconButton>
                    {active?.startDate && (
                        <TimeDistance
                            className="ml-4"
                            date={active.startDate}
                            period={EACH_SECOND}
                            withSeconds
                        />
                    )}
                    <span className="ml-4 truncate">
                        {active?.name ??
                            (loading.value ? "Loading..." : "No active timer")}
                    </span>
                </div>
                <div className="flex flex-col">
                    {visibleTiles.map((tile) => (
                        <div
                            key={tile.id}
                            className={cn(
                                "flex items-center px-3 py-2",
                                active?.id === tile.id && "bg-white/20",
                            )}
                        >
                            <IconButton
                                flat
                                onClick={getHandleTileClick(tile)}
                                className="p-2"
                            >
                                {active?.id === tile.id ? (
                                    <PauseIcon width="18" height="18" />
                                ) : (
                                    <PlayIcon width="18" height="18" />
                                )}
                            </IconButton>
                            <span className="ml-4 truncate">{tile.name}</span>
                        </div>
                    ))}
                    {visibleTiles.length > 0 && (
                        <button
                            type="button"
                            onClick={() => toggleTrackerPage(true)}
                            className="flex items-center justify-between rounded-b-lg border-t-2 border-zinc-600/20 p-3"
                        >
                            <div className="flex items-center">
                                <div className="h-[35px] w-[35px]" />
                                <span className="ml-4">
                                    Open detailed tracker
                                </span>
                            </div>
                            <ChevronRightIcon
                                width="24"
                                height="24"
                                className="fill-gray-300"
                            />
                        </button>
                    )}
                </div>
                {!loading.value && visibleTiles.length === 0 && (
                    <div className="mb-3 p-3 text-center text-white">
                        <p>You don't have any timers yet.</p>
                        <p>
                            Go to your{" "}
                            <Link
                                to={`${me("profile")}?tab=timer`}
                                onClick={() => {
                                    setTimeout(() => toggleTracker(false), 300);
                                }}
                                className="text-sky-300 underline"
                            >
                                profile
                            </Link>{" "}
                            to create one.
                        </p>
                    </div>
                )}
            </div>
        </Transition>,
        target.value,
    );
};

export default Popup;
